import React, { useEffect, useState } from "react";
import { DashboardLayout } from "./components/DashboardLayout";
import { Button } from "../../components/Button";
import { IApiChart, IChart } from "../../@types/chart.type";
import { SidePanel } from "./components/SidePanel";
import { listRepresentationWidgetsMock } from "../../mock/widgetStructure";
import iconAdd from "./../../assets/_AddCircleIcon_.svg";
import image from "./../../assets/image_pageInitial.svg";
import { useConfirm } from "../../hooks/useConfirm";
import { getPageHeight } from "./utils";
import TutorialModal from "./components/TutorialModal";
import { storageUserGet } from "../../storage/storageUser";
import {
  addOrUpdateWidgetUser,
  deleteWidgetUser,
  getAllWidgetsByUserId,
} from "../../services/api/widgetsUser/widgetsUserService";
import secureLocalStorage from "react-secure-storage";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import { useBlocker } from "react-router-dom";
import styles from "./styles.module.scss";
import { isEqual } from "lodash";

const TUTORIALMODAL_STORAGE = "@projectManage:tutorialModal";

export const PageInitial: React.FC = () => {
  const [chartsFront, setChartsFront] = useState<IChart[]>([]);
  const [chartsFrontCopyAux, setChartsFrontCopyAux] = useState<IChart[]>([]);
  const [listOfChartsToRemove, setListOfChartsToRemove] = useState<IChart[]>(
    []
  );
  const [chartSelected, setChartSelected] = useState<IChart>({} as IChart);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [ismodeEdit, setIsModeEdit] = useState(false);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [openModalConfirmCancelEdit, setOpenModalConfirmCancelEdit] =
    useState(false);
  const [openTutorialModal, setOpenTutorialModal] = useState(
    JSON.parse(secureLocalStorage.getItem(TUTORIALMODAL_STORAGE) as string) ===
      null
      ? true
      : false
  );

  const user = storageUserGet();

  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);

  const confirmDeletion = useConfirm(
    "Tem certeza que deseja excluir a página?",
    openModalConfirm,
    handleCloseModalConfirm,
    "Esta ação não pode ser desfeita."
  );

  const handleOpenModalConfirmCancelEdit = () =>
    setOpenModalConfirmCancelEdit(true);
  const handleCloseModalConfirmCancelEdit = () =>
    setOpenModalConfirmCancelEdit(false);

  const confirmCancelEditing = useConfirm(
    "Tem certeza que deseja cancelar a edição?",
    openModalConfirmCancelEdit,
    handleCloseModalConfirmCancelEdit,
    "As alterações não salvas serão perdidas."
  );

  const toggleDrawer = (newOpen: boolean) => {
    setOpenDrawer(newOpen);
  };

  const theChartsAreTheSame = isEqual(chartsFront, chartsFrontCopyAux);
  const blockerNavigation = useBlocker(ismodeEdit && !theChartsAreTheSame);

  async function fetchAllWidgetsByUserId() {
    try {
      setIsLoading(true);

      if (user.id) {
        const responseWidgets = await getAllWidgetsByUserId(user.id);

        if (responseWidgets.statusCode === 200) {
          const mapedWidgets = responseWidgets.result?.map(
            (widget: IApiChart): IChart => {
              return {
                id: widget?.id,
                userId: widget.userId,
                widgetId: widget.widgetId,
                widgetName: widget?.widgetName,
                description: null,
                size: null,
                type: null,
                layout: {
                  i: widget?.widgetId,
                  h: widget?.height,
                  w: widget?.width,
                  x: widget?.x,
                  y: widget?.y,
                },
              };
            }
          );

          if (mapedWidgets) {
            setChartsFront(mapedWidgets);
            setChartsFrontCopyAux(mapedWidgets);
          }
        } else {
          toast.error(
            "Não foi possível carregar os widgets! Tente novamente mais tarde."
          );
        }
      }
    } catch (error) {
      console.log(error);
      toast.error(
        "Não foi possível carregar os widgets! Tente novamente mais tarde."
      );
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchAllWidgetsByUserId();
  }, []);

  function handleCloseTutorialModal() {
    setOpenTutorialModal(false);
    secureLocalStorage.setItem(TUTORIALMODAL_STORAGE, JSON.stringify(false));
  }

  const saveOrEditDashboard = async () => {
    if (!ismodeEdit) {
      setIsModeEdit(!ismodeEdit);
      return;
    }

    try {
      if (ismodeEdit) {
        setIsLoading(true);

        if (listOfChartsToRemove.length) {
          await Promise.all(
            listOfChartsToRemove.map(async (chart) => {
              if (chart.id) {
                await deleteWidgetUser(chart.id);
              }

              return chart;
            })
          );

          setListOfChartsToRemove([]);
        }

        await Promise.all(
          chartsFront.map(async (chart) => {
            if (chart.id) {
              await addOrUpdateWidgetUser({
                id: chart.id,
                widgetId: chart.widgetId,
                widgetName: chart.widgetName,
                userId: user.id,
                x: chart.layout.x,
                y: chart.layout.y,
                width: chart.layout.w,
                height: chart.layout.h,
              });
            } else {
              await addOrUpdateWidgetUser({
                widgetId: chart.widgetId,
                widgetName: chart.widgetName,
                userId: user.id,
                x: chart.layout.x,
                y: chart.layout.y,
                width: chart.layout.w,
                height: chart.layout.h,
              });
            }

            return chart;
          })
        );
        await fetchAllWidgetsByUserId();
        setIsModeEdit(false);
        toast.success("página salva/atualizada com sucesso!");
      }
    } catch (error) {
      toast.error("Erro ao salvar a página! Tente novamente mais tarde.");
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDashboard = async () => {
    handleOpenModalConfirm();
    const isConfirm = await confirmDeletion.showConfirm();

    if (isConfirm) {
      try {
        setIsLoading(true);
        await Promise.all(
          chartsFront.map(async (chart) => {
            if (chart.id) {
              await deleteWidgetUser(chart.id);
            }

            return chart;
          })
        );

        await fetchAllWidgetsByUserId();
        setIsModeEdit(false);
        toast.success("Página excluída com sucesso!");
      } catch (error) {
        toast.error("Error ao excluir a página! Tente novamente mais tarde.");
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    async function cancelEditByRouterDom() {
      if (blockerNavigation.state !== "blocked") return;

      handleOpenModalConfirmCancelEdit();
      const proceed = await confirmCancelEditing.showConfirm();
      if (proceed) blockerNavigation.proceed();
      else blockerNavigation.reset();
    }

    cancelEditByRouterDom();
  }, [blockerNavigation]);

  const cancelEdit = async () => {
    if (ismodeEdit && !theChartsAreTheSame) {
      handleOpenModalConfirmCancelEdit();
      const isConfirm = await confirmCancelEditing.showConfirm();

      if (isConfirm) {
        await fetchAllWidgetsByUserId();
        setIsModeEdit(false);
      }
    } else {
      setIsModeEdit(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (ismodeEdit && !theChartsAreTheSame) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [ismodeEdit, theChartsAreTheSame]);

  return (
    <>
      <div style={{ display: "flex" }} className={styles.pageInitial}>
        {isLoading ? (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <CircularProgress size={100} />
          </div>
        ) : (
          <>
            {chartsFront.length > 0 || ismodeEdit ? (
              <div className={styles.dashboard}>
                <div className={styles.titlesAndBtnPublished}>
                  <div style={{ display: "flex", gap: "1rem" }}>
                    {ismodeEdit && (
                      <Button
                        variant="outlineRed"
                        onClick={() => deleteDashboard()}
                        content="Excluir página"
                        disabled={
                          isLoading || (ismodeEdit && chartsFront.length === 0)
                        }
                      />
                    )}

                    {ismodeEdit && (
                      <Button
                        variant="cancelPurple"
                        onClick={() => cancelEdit()}
                        content="Cancelar edição"
                      />
                    )}

                    <Button
                      variant="solidPurple"
                      content={
                        ismodeEdit ? "Salvar página" : "Editar Página Inicial"
                      }
                      onClick={saveOrEditDashboard}
                      disabled={
                        isLoading ||
                        (ismodeEdit &&
                          chartsFront.length === 0 &&
                          listOfChartsToRemove.length === 0)
                      }
                    />
                  </div>
                </div>

                <>
                  {ismodeEdit && (
                    <Button
                      variant="solid"
                      style={{ marginLeft: "auto", fontWeight: "700" }}
                      onClick={() => toggleDrawer(true)}
                      iconUrl={iconAdd}
                      content="Adicionar gráfico"
                    />
                  )}
                  <SidePanel
                    setChartSelected={setChartSelected}
                    open={openDrawer}
                    toggleDrawer={() => toggleDrawer(false)}
                    listRepresentationCharts={listRepresentationWidgetsMock}
                  />
                  <div
                    style={{ height: getPageHeight() }}
                    className={styles.container}
                  >
                    <DashboardLayout
                      chartsFront={chartsFront}
                      setChartsFront={setChartsFront}
                      setListOfChartsToRemove={setListOfChartsToRemove}
                      listOfChartsToRemove={listOfChartsToRemove}
                      chartSelected={chartSelected}
                      ismodeEdit={ismodeEdit}
                    />
                  </div>
                </>
              </div>
            ) : (
              <>
                <div className={styles.masterPage}>
                  <div className={styles.image}>
                    <img src={image} alt="" />
                  </div>
                  <div className={styles.description}>
                    <h3>Seja bem-vindo ao Gerenciador de Projetos</h3>
                    <p>
                      Personalize sua página inicial! Clique no botão abaixo
                      para começar.
                    </p>

                    <Button
                      style={{ fontSize: "1.2rem" }}
                      onClick={() => setIsModeEdit(true)}
                      variant="solidPurple"
                      content="Personalizar página inicial"
                    />
                  </div>
                </div>
                <TutorialModal
                  openTutorialModal={openTutorialModal}
                  setCloseTutorialModal={() => handleCloseTutorialModal()}
                />
              </>
            )}
          </>
        )}
      </div>
      {confirmDeletion.Confirm}
      {confirmCancelEditing.Confirm}
    </>
  );
};
